<script setup lang="ts">
// TODO :: TASK-2103 - Should be promoted to an organism, maybe even a template
import { computed, ref } from 'vue';
import type { ProductAtCart } from '@ProductsAndServicesModule';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useAToast, useADialog } from '@Atoms';
import { applyShelterCurrency } from '@Helpers';
import useCartStore from '../../../store';
import { getRetailLinePriceEditDisplayHelper } from '../../../helpers';
import { AddProductToOutcomeCartService, ApplyCustomItemDiscountService } from '../../../services';

export interface RetailRow {
  formattedAmount: string;
  formattedOriginalAmount: string;
  hasDiscount: boolean;
  meta: { type: 'product' | 'service'; index: number; _id?: string };
  name: string;
  originalAmount: number | null;
  quantity: number;
  rawItem: ProductAtCart;
  subtotal: number;
}

const props = withDefaults(
  defineProps<{
    canEditItems?: boolean;
    isEditingProducts: boolean;
    products: ProductAtCart[];
    services: ProductAtCart[];
  }>(),
  { canEditItems: true }
);

const store = useCartStore();
const { cartData } = storeToRefs(store);

const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { notify, TOAST_TYPES } = useAToast();
const { t } = useI18n();

const TOAST_REMOVE_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};
const TOAST_QUANTITY_UPDATED = {
  title: 'cart.message.itemQuantityUpdated',
  type: TOAST_TYPES.SUCCESS,
};

const editingRowKey = ref<string | null>(null);
const updatingAmountKey = ref<string | null>(null);
const updatingQuantityKey = ref<string | null>(null);

const retailList = computed((): RetailRow[] => {
  const rows: RetailRow[] = [];
  const products = props.products ?? [];
  const services = props.services ?? [];

  products.forEach((product, productIndex) => {
    const quantity = typeof product.quantity === 'number' ? product.quantity : 1;
    const display = getRetailLinePriceEditDisplayHelper({ ...product, quantity });
    if (!display) return;

    const { lineAmount, originalLineTotal, showComparison } = display;
    const hasDiscount = showComparison;
    const originalAmount = showComparison ? originalLineTotal : null;

    rows.push({
      name: product.product || t('cart.summary.product'),
      quantity,
      subtotal: lineAmount,
      formattedAmount: formatAmount(lineAmount),
      hasDiscount,
      originalAmount,
      formattedOriginalAmount: originalAmount != null ? formatAmount(originalAmount) : '',
      meta: { type: 'product', index: productIndex, _id: product._id },
      rawItem: product,
    });
  });

  services.forEach((service, serviceIndex) => {
    const quantity = typeof service.quantity === 'number' ? service.quantity : 1;
    const display = getRetailLinePriceEditDisplayHelper({ ...service, quantity });
    if (!display) return;

    const { lineAmount, originalLineTotal, showComparison } = display;
    const hasDiscount = showComparison;
    const originalAmount = showComparison ? originalLineTotal : null;

    rows.push({
      name: service.product || t('cart.summary.service'),
      quantity,
      subtotal: lineAmount,
      formattedAmount: formatAmount(lineAmount),
      hasDiscount,
      originalAmount,
      formattedOriginalAmount: originalAmount != null ? formatAmount(originalAmount) : '',
      meta: { type: 'service', index: serviceIndex, _id: service._id },
      rawItem: service,
    });
  });

  return rows;
});

function getRowKey(row: RetailRow): string {
  return `${row.meta.type}-${row.meta.index}`;
}

function formatAmount(value: number): string {
  return value === 0 ? applyShelterCurrency(0) : applyShelterCurrency(value);
}

// EVENTS
function onCloseAmountEdit() {
  editingRowKey.value = null;
}

function onProductQuantityUpdate({ meta, quantity, rawItem }: RetailRow, delta: 1 | -1) {
  if (updatingQuantityKey.value !== null) return;

  const key = `${meta.type}-${meta.index}`;
  const isLastItem = delta === -1 && quantity <= 1;
  if (isLastItem) return;

  updatingQuantityKey.value = key;

  AddProductToOutcomeCartService({
    products: [{ ...rawItem, quantity: delta }],
  })
    .then((data) => notify(TOAST_QUANTITY_UPDATED) && (cartData.value = data))
    .catch(() => notify(TOAST_REMOVE_ERROR))
    .finally(() => (updatingQuantityKey.value = null));
}

function onRemoveProductRequest(_event: MouseEvent, row: RetailRow) {
  const isProductsOnlyCart = cartData.value?.cart_type === 'product' || cartData.value?.cart_type === 'service';
  const isLastItemInProductsOnlyCart = isProductsOnlyCart && retailList.value.length === 1;

  activeModal.value = SYSTEM_MODALS.CART_REMOVE_RETAIL_ITEM;
  activeModalProps.value = { row, isLastItemInProductsOnlyCart };
  dialogVisibility.value = true;
}

function onEditProductRequest(_event: MouseEvent, row: RetailRow) {
  editingRowKey.value = getRowKey(row);
}

function onConfirmAmountEdit(payload: { amount: number; discountType: 'percent' | 'fixed'; discount?: number }) {
  if (editingRowKey.value === null) return;

  const row = retailList.value.find((row) => getRowKey(row) === editingRowKey.value);
  if (!row?.rawItem?._id) {
    onCloseAmountEdit();
    return;
  }

  updatingAmountKey.value = editingRowKey.value;

  const PAYLOAD = {
    item_id: row.rawItem._id,
    target_item: row.meta.type === 'service' ? 'services' : 'products',
    amount: Math.max(0, payload.amount),
    discount_type: payload.discountType,
    ...(payload.discount != null && { discount: payload.discount }),
  };

  ApplyCustomItemDiscountService(PAYLOAD)
    .then(({ data }) => {
      cartData.value = data;
      onCloseAmountEdit();
    })
    .catch(() => notify(TOAST_REMOVE_ERROR))
    .finally(() => (updatingAmountKey.value = null));
}

function getRetailRowClass(row: RetailRow) {
  return [
    'flex first:pt-0',
    props.isEditingProducts && getRowKey(row) !== editingRowKey.value
      ? 'flex-col sm:flex-row md:flex-row lg:flex-row'
      : '',
    props.isEditingProducts && getRowKey(row) === editingRowKey.value ? 'flex-col 3xl:flex-row' : '',
  ];
}
</script>

<template>
  <div class="text- mt-4">
    <ul class="flex flex-col gap-2">
      <li
        v-for="row in retailList"
        :class="getRetailRowClass(row)"
        :key="`${row.meta.type}-${row.meta.index}`"
      >
        <span class="mb-2 flex-1 text-sm lg:mb-0">
          {{ row.name }}
        </span>

        <div
          v-if="isEditingProducts"
          :class="[
            'flex w-fit shrink-0 justify-end',
            getRowKey(row) === editingRowKey
              ? 'mb-2 mt-2 w-full lg:mt-6 2xl:mb-0 2xl:mt-0 2xl:w-auto'
              : 'items-center gap-0',
          ]"
        >
          <template v-if="getRowKey(row) === editingRowKey">
            <AShoppingCartEditAmount
              stacked
              label=""
              :closeOnConfirm="false"
              :currentAmount="row.quantity > 0 ? row.subtotal / row.quantity : row.subtotal"
              :hidePencil="true"
              :isEditingAnimals="isEditingProducts"
              :key="`edit-${getRowKey(row)}`"
              :originalAmount="row.originalAmount != null ? row.originalAmount / row.quantity : null"
              :quantity="row.quantity"
              :startInEditMode="true"
              :unitLabel="$t('cart.summary.ea')"
              :updating="updatingAmountKey === getRowKey(row)"
              @close="onCloseAmountEdit()"
              @onUpdateRequest="onConfirmAmountEdit"
            >
              <template #beforeTotal>
                <AProductQuantityStepper
                  compact
                  :quantity="row.quantity"
                  :disabledMinus="
                    updatingQuantityKey === getRowKey(row) || updatingAmountKey === getRowKey(row) || row.quantity <= 1
                  "
                  :disabledPlus="updatingQuantityKey === getRowKey(row) || updatingAmountKey === getRowKey(row)"
                  @onMinus="onProductQuantityUpdate(row, -1)"
                  @onPlus="onProductQuantityUpdate(row, 1)"
                />
              </template>
            </AShoppingCartEditAmount>
          </template>

          <template v-else>
            <AProductQuantityStepper
              compact
              :quantity="row.quantity"
              :disabledMinus="updatingQuantityKey === getRowKey(row) || updatingAmountKey !== null || row.quantity <= 1"
              :disabledPlus="updatingQuantityKey === getRowKey(row) || updatingAmountKey !== null"
              @onMinus="onProductQuantityUpdate(row, -1)"
              @onPlus="onProductQuantityUpdate(row, 1)"
            />

            <AShoppingCartAmountDisplay
              :amount="row.formattedAmount"
              :originalAmount="row.formattedOriginalAmount || null"
              :hasDiscount="row.hasDiscount"
            />

            <AShoppingCartIconButton
              v-if="canEditItems"
              icon="pi pi-pencil"
              :disabled="updatingAmountKey !== null"
              @onClick="onEditProductRequest($event, row)"
            />

            <AShoppingCartIconButton
              icon="pi pi-trash"
              :disabled="updatingAmountKey !== null"
              @onClick="onRemoveProductRequest($event, row)"
            />
          </template>
        </div>

        <template v-else>
          <AShoppingCartAmountDisplay
            :amount="row.formattedAmount"
            :originalAmount="row.formattedOriginalAmount || null"
            :hasDiscount="row.hasDiscount"
            :wrapperClass="'flex shrink-0 flex-col items-end text-right'"
          />
        </template>
      </li>
    </ul>
  </div>
</template>
